import { Timestamp } from 'firebase/firestore';
import moment from 'moment';

export interface FSMenu {
  data: string;
  venue_id: number;
}

export interface FSMenuAddendum {
  data: string;
  venue_id: number;
  date?: Timestamp;
}

export interface FSMenu2 {
  // the visual structure
  pages: string;
  // the product database
  products: string;
  // menu packages
  packages: string;
  // meta info
  meta: string;
  venue_id: number;
}

export interface MenuStructure {
  pages: MenuPage[];
  food: MenuSection[];
  drinks: MenuSection[];
  donation_items: MenuItem[];
}

export interface MenuPage {
  id: string;
  name: string;
  pic: string;
  sections: MenuSection[];
  index: number;
}

export interface MenuSection {
  id: string;
  name: string;
  desc: string;
  pic: string;
  categories: MenuCategory[];
  index: number;

  mhide?: boolean;
  lang?: string;
}

export interface MenuCategory {
  id: string;
  type: number;
  name: string;
  desc: string;
  pic: string;
  class: string;
  items: MenuItem[];
  index: number;
  phase: number;

  mhide?: boolean;
  whide?: boolean;

  lang?: string;
}

export interface MenuItem {
  id: string;
  name: string;
  price: string;
  prices?: MenuItemPriceListPrices[];
  desc: string;
  formatted: string;
  class: string;
  ic: string;
  alc: boolean;
  pic: string;
  vat: number;
  phase: number;
  phaseid: string;
  wait: number;
  use_scale: boolean;
  tare: number;
  pc: string;
  hidden: boolean;
  when: WhenFilter[];
  pushes: MenuItemPush[];
  form: "f"|"d";
  deadline?: DeadlineFilter;
  post_actions?: string;

  attributes: MenuItemAttribute[];
  contains: MenuItemContain[];
  adjusts: MenuItemAdjust[];
  tags: string[];
  mhide?: boolean;
  whide?: boolean;
  lang?: string;
  package?: MenuPackage;
  takeaway_formatted?: string;

  // Translucent
  currentOrderCount: number;
  currentStockState: boolean;
}

export interface MenuPackage {
  included: MenuPackageItem[];
  choices?: MenuPackageChoice[];
  display: string[];
  desc: string;
  items: string[];
  index: number;
  lang?: string;
}

export interface MenuPackageItem {
  name: string;
  id: string;
  price: number;
  display: string[];
  phase?: number;
  phaseid?: string;
}

// A group in a package where the guest picks exactly `count` options (the same one may repeat).
// Option prices are absolute: the surcharge over the menu price is price - base.
export interface MenuPackageChoice {
  title: string;
  count: number;
  base: number;
  options: MenuPackageItem[];
}

// A guest's selection on an item: radio/checkbox value per attribute id, or option key -> count for a multi one
export type AttrValues = Record<string, string | boolean | Record<string, number>>;

// One part of a package being ordered: a fixed part (choiceIndex undefined) or a pick from a choice
export interface PackagePart {
  key: string;
  id: string;
  name: string;
  menuItem: MenuItem | null;
  choiceIndex?: number;
  surcharge?: number;
  attrValues?: AttrValues;
}

// What the guest selected on an ordered item, decoded from its id: own attribute values, picks per choice
// (option id -> count) and each package part's attribute values by part key
export interface DecodedItemSelection {
  attrValues: AttrValues;
  pkgCounts: Record<string, number>[];
  partValues: Record<string, AttrValues>;
}

export interface MenuItemPriceListPrices {
  name: string;
  price: string;
  takeaway_price?: string;
}

export interface MenuItemAttributeMulti {
  min: number;
  max: number;
  unit: string
}

export interface MenuItemAttribute {
  id: string;
  options: MenuItemOption[];
  hidden: boolean;
  forced?: boolean;
  multi?: MenuItemAttributeMulti;
}

export interface MenuItemOption {
  price: number;
  price_diff: string;
  key: string;
  formatted: string;
  value: string;
  desc: string;
  default: boolean;
  contains: MenuItemContain[];
  lang?: string;
}

export interface MenuItemContain {
  del: boolean;
  id: string;
  name: string;
  info: string;
}

export interface MenuItemAdjust {
  id: string;
  name: string;
}

export let EMPTY_WHEN_FILTER = {};
export interface WhenFilter {
  // [1,2,3], bt30: "11:00", et30: "13:00", bd: "20180307", ed: "20180407"
  bt30?: string;
  et30?: string;
  wds?: number[];
  bd?: string;
  ed?: string;
}

export interface DeadlineFilter extends WhenFilter {
  rel_days?: number;
  rel_hours?: number;
}

export interface MenuItemPush {
  index: number;
  title: string;
  groups: MenuItemPushGroup[];
  included: boolean;
  mandatory: boolean;
}

export interface MenuItemPushGroup {
  title: string;
  items: string[];

  // Resolved in run time
  menuItems: MenuItem[];
}

export interface MenuAddendum {
  items: MenuAddendumItem[];
}

export interface MenuAddendumItem {
  id: string;
  name: string;
  inventory_count: number;
  is_inventoried: boolean;
  out_of_stock: boolean;
  updated: string;
}

export class DateSpan {
  start: moment.Moment;
  end: moment.Moment;

  constructor(start: moment.Moment, end: moment.Moment) {
    this.start = start;
    this.end = end;
  }
}

export class PostActions {
  postActions?: PostAction[];

  constructor(postActionsJson?: string) {
    if (postActionsJson) {
      this.postActions = JSON.parse(postActionsJson) as PostAction[];
    }
  }

  getPostAction(action: string): PostAction | undefined {
    return this.postActions?.find(pa => pa?.action === action);
  }
}

export class PostAction {
  action?: string;
  card?: string;
}

export interface PushPage {
  item: MenuItem;
  pushIndex: number;
  rid: string;
  // What the guest ticked, kept with the sheet: the push page is rebuilt when a package's pick sheet closes
  state?: PushSelection;
}

export interface PushSelection {
  selectedItems: Record<string, boolean>;
  attrValues: Record<string, any>;
  picked: Record<string, PickedPackage>;
}

// A package picked from a push page or the cart's suggestions: the item id with its picks, what was picked and the price
export interface PickedPackage {
  id: string;
  lines: string[];
  price: number;
}

// Item view mode "pick": choose a package's options for a push page and hand them back instead of adding
export interface PackagePick {
  startId?: string;
  included?: boolean;
  done: (picked: PickedPackage) => void;
}
